import { StorageData, AssetsData } from "@/types/metrics";
import { SHEET_TABS } from "@/lib/constants";
import { AssetPerformanceDetailed } from "@/types/asset-performance";

// Core interfaces for processed asset performance data
export interface AssetPerformanceMetrics {
  assetId: string;
  assetType: string;
  assetFieldType: string;
  assetText?: string;
  assetName?: string;
  assetImageUrl?: string;
  assetYoutubeVideoId?: string;

  // Performance metrics
  impressions: number;
  clicks: number;
  cost: number;
  conversions: number;
  value: number;
  allConv: number;
  allValue: number;

  // Calculated metrics
  ctr: number;
  cvr: number;
  roas: number;
  cpa: number;
  cpc: number;

  // Context information
  campaignTypes: string[];
  campaignCount: number;
  adGroupCount: number;
  dateRange: {
    start: string;
    end: string;
  };
}

export interface AssetContextPerformance {
  assetId: string;
  assetInfo: {
    type: string;
    fieldType: string;
    text?: string;
    name?: string;
    imageUrl?: string;
  };

  // Performance by campaign type
  searchPerformance?: AssetPerformanceMetrics;
  demandGenPerformance?: AssetPerformanceMetrics;
  pmaxPerformance?: AssetPerformanceMetrics;
  shoppingPerformance?: AssetPerformanceMetrics;
  displayPerformance?: AssetPerformanceMetrics;

  // Overall performance
  totalPerformance: AssetPerformanceMetrics;

  // Context insights
  bestContext: string;
  worstContext: string;
  utilizationGap: number; // percentage of potential contexts not being used
}

export interface FieldTypeEffectiveness {
  fieldType: string;
  assetCount: number;
  avgCtr: number;
  avgCvr: number;
  avgRoas: number;
  avgCpa: number;
  totalImpressions: number;
  totalCost: number;
  totalValue: number;

  // Statistical significance
  sampleSize: number;
  confidenceLevel: "high" | "medium" | "low";
}

export interface PinnedAssetAnalysis {
  assetId: string;
  assetInfo: {
    type: string;
    fieldType: string;
    text?: string;
    name?: string;
  };

  pinnedPerformance: AssetPerformanceMetrics;
  rotatedPerformance: AssetPerformanceMetrics;

  // Comparison
  pinnedIsBetter: boolean;
  performanceDifference: {
    ctrDiff: number;
    cvrDiff: number;
    roasDiff: number;
  };

  recommendation: "pin" | "unpin" | "test_more";
  confidence: "high" | "medium" | "low";
}

export type AssetAnalysisResult = ReturnType<
  typeof AssetPerformanceProcessor.processAssetPerformance
>;

export class AssetPerformanceProcessor {
  private static readonly MIN_IMPRESSIONS_FOR_ANALYSIS = 100;
  private static readonly MIN_COST_FOR_ANALYSIS = 5;

  /**
   * Normalizes asset types to standard categories
   */
  private static normalizeAssetType(assetType: string): string {
    if (!assetType) return "UNKNOWN";

    const type = assetType.toUpperCase();

    // Video types
    if (type.includes("VIDEO") || type.includes("YOUTUBE")) {
      return "YOUTUBE_VIDEO";
    }

    // Image types
    if (
      type.includes("IMAGE") ||
      type.includes("LOGO") ||
      type.includes("CAROUSEL") ||
      type.includes("DEMAND_GEN_CAROUSEL_CARD")
    ) {
      return "IMAGE";
    }

    // Basic text types (headlines, descriptions)
    if (
      type === "TEXT" ||
      type.includes("HEADLINE") ||
      type.includes("DESCRIPTION")
    ) {
      return "TEXT";
    }

    // Preserve extension types and other specific types as-is
    // These include: SITELINK, CALLOUT, STRUCTURED_SNIPPET, PRICE, PROMOTION, CALL, CALL_TO_ACTION, etc.
    return type;
  }

  /**
   * Main processing function that joins all asset data sources
   */
  static processAssetPerformance(data: StorageData): {
    topPerformers: AssetPerformanceMetrics[];
    bottomPerformers: AssetPerformanceMetrics[];
    contextAnalysis: AssetContextPerformance[];
    fieldTypeEffectiveness: FieldTypeEffectiveness[];
    pinnedAnalysis: PinnedAssetAnalysis[];
    allAssets: AssetPerformanceDetailed[];
    rawJoinedData: any[];
  } {
    if (!data) {
      return {
        topPerformers: [],
        bottomPerformers: [],
        contextAnalysis: [],
        fieldTypeEffectiveness: [],
        pinnedAnalysis: [],
        allAssets: [],
        rawJoinedData: [],
      };
    }

    try {
      const joinedAssetData = this.joinAssetDataSources(data);
      const limitedJoinedData = joinedAssetData;
      const allAssetMetrics = this.calculateAssetMetrics(limitedJoinedData);
      const contextAnalysis = this.analyzeAssetContext(
        limitedJoinedData,
        data[SHEET_TABS.ASSETS] || [],
      );
      const fieldTypeEffectiveness =
        this.analyzeFieldTypeEffectiveness(allAssetMetrics);
      const pinnedAnalysis = this.analyzePinnedAssets(limitedJoinedData);

      // Create allAssets list
      const allAssetsList = this.createDetailedAssetList(
        joinedAssetData,
        data[SHEET_TABS.ASSETS] || [],
      );

      // Sort and filter top/bottom performers
      const validAssets = allAssetMetrics.filter(
        (asset) => asset.impressions >= this.MIN_IMPRESSIONS_FOR_ANALYSIS,
      );

      const topPerformers = validAssets.sort((a, b) => b.roas - a.roas);

      const bottomPerformers = validAssets
        .filter((asset) => asset.cost >= this.MIN_COST_FOR_ANALYSIS)
        .sort((a, b) => a.roas - b.roas);

      return {
        topPerformers,
        bottomPerformers,
        contextAnalysis,
        fieldTypeEffectiveness,
        pinnedAnalysis,
        allAssets: allAssetsList,
        rawJoinedData: joinedAssetData,
      };
    } catch (error) {
      console.error("Error processing asset performance data:", error);
      return {
        topPerformers: [],
        bottomPerformers: [],
        contextAnalysis: [],
        fieldTypeEffectiveness: [],
        pinnedAnalysis: [],
        allAssets: [],
        rawJoinedData: [],
      };
    }
  }

  /**
   * Limits data to first N unique assets for development performance
   */
  private static limitDataForDevelopment(
    joinedData: any[],
    maxAssets: number,
  ): any[] {
    const uniqueAssetIds = new Set<string>();
    const limitedData: any[] = [];

    for (const row of joinedData) {
      if (!row || !row.assetId) continue;

      if (uniqueAssetIds.size < maxAssets) {
        uniqueAssetIds.add(row.assetId);
        limitedData.push(row);
      } else if (uniqueAssetIds.has(row.assetId)) {
        // Include all rows for assets we're already tracking
        limitedData.push(row);
      }
    }

    return limitedData;
  }

  /**
   * Joins data from ad_assets, camp_assets, pmax_assets with assets table
   */
  private static joinAssetDataSources(data: StorageData) {
    const joinedData: any[] = [];

    // Consolidate all asset-related data sources
    // NOTE: We're only using ad_assets for now.
    // camp_assets and pmax_assets will be handled in a separate Performance Max tab later
    // const campAssets = data[SHEET_TABS.CAMP_ASSETS] || [];
    // const pmaxAssets = data[SHEET_TABS.PMAX_ASSETS] || [];
    // const agAssets = data[SHEET_TABS.AG_ASSETS] || [];

    // Asset details (lookup table)
    const assetsLookup = new Map<string, any>();
    (data[SHEET_TABS.ASSETS] || []).forEach((asset) => {
      if (asset && asset.assetId) {
        assetsLookup.set(asset.assetId, asset);
      }
    });

    // Process Ad Assets
    const adAssets = data[SHEET_TABS.AD_ASSETS] || [];
    adAssets.forEach((adAsset) => {
      if (!adAsset || !adAsset.assetId) return;
      const assetDetail = assetsLookup.get(adAsset.assetId);

      joinedData.push({
        ...adAsset,
        assetName: assetDetail?.assetName,
        assetText: assetDetail?.assetText,
        assetImageUrl: assetDetail?.assetImageUrl,
        assetYoutubeVideoId: assetDetail?.assetYoutubeVideoId,
        assetYoutubeVideoTitle: assetDetail?.assetYoutubeVideoTitle,
        // Add source context
        sourceContext: "Ad",
      });
    });

    // Process Ad Group Assets (from responsive search/display ads etc.)
    // agAssets.forEach(agAsset => { // Temporarily removing to fix type error
    //      if (!agAsset || !agAsset.adId) return;
    //      // Need to look up assetId from ad_assets perhaps, or assume this structure is different.
    //      // For now, let's assume agAsset might have assetId directly or needs another join step.
    //      // This part might need refinement based on actual AG_ASSETS structure and linkage.
    //      // If agAsset provides assetId directly:
    //      if(agAsset.assetId) { // Placeholder check
    //         const assetDetail = assetsLookup.get(agAsset.assetId);
    //         joinedData.push({
    //             ...agAsset, // cost, impr etc are on agAsset itself
    //             assetName: assetDetail?.assetName,
    //             assetText: assetDetail?.assetText,
    //             assetImageUrl: assetDetail?.assetImageUrl,
    //             assetYoutubeVideoId: assetDetail?.assetYoutubeVideoId,
    //             assetYoutubeVideoTitle: assetDetail?.assetYoutubeVideoTitle,
    //             sourceContext: 'AdGroup Ad',
    //         });
    //      }
    // });

    return joinedData;
  }

  /**
   * Calculates aggregated metrics for each asset
   */
  private static calculateAssetMetrics(
    joinedData: any[],
  ): AssetPerformanceMetrics[] {
    const assetGroups = new Map<string, any[]>();

    // Group by assetId with null safety
    joinedData.forEach((row) => {
      if (!row || !row.assetId) return;

      if (!assetGroups.has(row.assetId)) {
        assetGroups.set(row.assetId, []);
      }
      assetGroups.get(row.assetId)!.push(row);
    });

    const metrics: AssetPerformanceMetrics[] = [];

    assetGroups.forEach((rows, assetId) => {
      // Skip if no valid rows or assetId
      if (!rows?.length || !assetId) return;

      const representative = rows[0];
      if (!representative) return;

      // Aggregate metrics with null safety
      const totalImpressions = rows.reduce(
        (sum, row) => sum + (Number(row?.impressions) || 0),
        0,
      );
      const totalClicks = rows.reduce(
        (sum, row) => sum + (Number(row?.clicks) || 0),
        0,
      );
      const totalCost = rows.reduce(
        (sum, row) => sum + (Number(row?.cost) || 0),
        0,
      );
      const totalConversions = rows.reduce(
        (sum, row) => sum + (Number(row?.conversions) || 0),
        0,
      );
      const totalValue = rows.reduce(
        (sum, row) => sum + (Number(row?.value) || 0),
        0,
      );
      const totalAllConv = rows.reduce(
        (sum, row) => sum + (Number(row?.allConv) || 0),
        0,
      );
      const totalAllValue = rows.reduce(
        (sum, row) => sum + (Number(row?.allValue) || 0),
        0,
      );

      // Calculate derived metrics
      const ctr =
        totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0;
      const cvr = totalClicks > 0 ? (totalConversions / totalClicks) * 100 : 0;
      const roas = totalCost > 0 ? totalValue / totalCost : 0;
      const cpa = totalConversions > 0 ? totalCost / totalConversions : 0;
      const cpc = totalClicks > 0 ? totalCost / totalClicks : 0;

      // Get context information with null safety
      const campaignTypes = [
        ...new Set(rows.map((row) => row?.campType).filter(Boolean)),
      ];
      const campaignCount = new Set(
        rows.map((row) => row?.campId).filter(Boolean),
      ).size;
      const adGroupCount = new Set(
        rows.map((row) => row?.adGroupId).filter(Boolean),
      ).size;

      // Get date range with null safety
      const dates = rows
        .map((row) => row?.segDate)
        .filter(Boolean)
        .sort();
      const dateRange = {
        start: dates[0] || "",
        end: dates[dates.length - 1] || "",
      };

      metrics.push({
        assetId,
        assetType: representative.assetType || "UNKNOWN",
        assetFieldType: representative.assetFieldType || "UNKNOWN",
        assetText: representative.assetText,
        assetName: representative.assetName,
        assetImageUrl: representative.assetImageUrl,
        assetYoutubeVideoId: representative.assetYoutubeVideoId,

        impressions: totalImpressions,
        clicks: totalClicks,
        cost: totalCost,
        conversions: totalConversions,
        value: totalValue,
        allConv: totalAllConv,
        allValue: totalAllValue,

        ctr,
        cvr,
        roas,
        cpa,
        cpc,

        campaignTypes,
        campaignCount,
        adGroupCount,
        dateRange,
      });
    });

    return metrics;
  }

  /**
   * Analyzes asset performance across different campaign contexts
   */
  private static analyzeAssetContext(
    joinedData: any[],
    assetsData: any[],
  ): AssetContextPerformance[] {
    const assetGroups = new Map<string, any[]>();

    // Group by assetId with null safety
    joinedData.forEach((row) => {
      if (!row || !row.assetId) return;

      if (!assetGroups.has(row.assetId)) {
        assetGroups.set(row.assetId, []);
      }
      assetGroups.get(row.assetId)!.push(row);
    });

    const contextAnalysis: AssetContextPerformance[] = [];

    assetGroups.forEach((rows, assetId) => {
      // Skip if no rows or invalid data
      if (!rows?.length || !assetId) return;

      const representative = rows[0];
      if (!representative) return;

      const assetInfo =
        assetsData.find((asset) => asset?.assetId === assetId) ||
        representative;
      if (!assetInfo) return;

      // Group by campaign type
      const byContext = new Map<string, any[]>();
      rows.forEach((row) => {
        if (!row) return;
        const context = row.campType || "UNKNOWN";
        if (!byContext.has(context)) {
          byContext.set(context, []);
        }
        byContext.get(context)!.push(row);
      });

      // Calculate performance for each context
      const contextPerformance: Record<string, AssetPerformanceMetrics> = {};
      let bestContext = "";
      let worstContext = "";
      let bestRoas = 0;
      let worstRoas = Infinity;

      byContext.forEach((contextRows, context) => {
        const metrics = this.calculateContextMetrics(
          contextRows,
          assetId,
          assetInfo,
        );
        contextPerformance[context] = metrics;

        if (
          metrics.roas > bestRoas &&
          metrics.impressions >= this.MIN_IMPRESSIONS_FOR_ANALYSIS
        ) {
          bestRoas = metrics.roas;
          bestContext = context;
        }
        if (
          metrics.roas < worstRoas &&
          metrics.impressions >= this.MIN_IMPRESSIONS_FOR_ANALYSIS
        ) {
          worstRoas = metrics.roas;
          worstContext = context;
        }
      });

      // Calculate total performance
      const totalPerformance = this.calculateContextMetrics(
        rows,
        assetId,
        assetInfo,
      );

      // Calculate utilization gap
      const availableContexts = [
        "SEARCH",
        "DEMAND_GEN",
        "PERFORMANCE_MAX",
        "SHOPPING",
        "DISPLAY",
      ];
      const usedContexts = Array.from(byContext.keys());
      const utilizationGap =
        ((availableContexts.length - usedContexts.length) /
          availableContexts.length) *
        100;

      contextAnalysis.push({
        assetId,
        assetInfo: {
          type: this.normalizeAssetType(
            assetInfo?.assetType || representative?.assetType || "UNKNOWN",
          ),
          fieldType: representative?.assetFieldType || "UNKNOWN",
          text: assetInfo?.assetText || representative?.assetText || null,
          name: assetInfo?.assetName || representative?.assetName || null,
          imageUrl:
            assetInfo?.assetImageUrl || representative?.assetImageUrl || null,
        },
        searchPerformance: contextPerformance["SEARCH"],
        demandGenPerformance: contextPerformance["DEMAND_GEN"],
        pmaxPerformance: contextPerformance["PERFORMANCE_MAX"],
        shoppingPerformance: contextPerformance["SHOPPING"],
        displayPerformance: contextPerformance["DISPLAY"],
        totalPerformance,
        bestContext,
        worstContext,
        utilizationGap,
      });
    });

    return contextAnalysis;
  }

  /**
   * Helper method to calculate metrics for a specific context
   */
  private static calculateContextMetrics(
    rows: any[],
    assetId: string,
    assetInfo: any,
  ): AssetPerformanceMetrics {
    const totalImpressions = rows.reduce(
      (sum, row) => sum + (Number(row?.impressions) || 0),
      0,
    );
    const totalClicks = rows.reduce(
      (sum, row) => sum + (Number(row?.clicks) || 0),
      0,
    );
    const totalCost = rows.reduce(
      (sum, row) => sum + (Number(row?.cost) || 0),
      0,
    );
    const totalConversions = rows.reduce(
      (sum, row) => sum + (Number(row?.conversions) || 0),
      0,
    );
    const totalValue = rows.reduce(
      (sum, row) => sum + (Number(row?.value) || 0),
      0,
    );
    const totalAllConv = rows.reduce(
      (sum, row) => sum + (Number(row?.allConv) || 0),
      0,
    );
    const totalAllValue = rows.reduce(
      (sum, row) => sum + (Number(row?.allValue) || 0),
      0,
    );

    const ctr =
      totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0;
    const cvr = totalClicks > 0 ? (totalConversions / totalClicks) * 100 : 0;
    const roas = totalCost > 0 ? totalValue / totalCost : 0;
    const cpa = totalConversions > 0 ? totalCost / totalConversions : 0;
    const cpc = totalClicks > 0 ? totalCost / totalClicks : 0;

    const campaignTypes = [
      ...new Set(rows.map((row) => row?.campType).filter(Boolean)),
    ];
    const campaignCount = new Set(
      rows.map((row) => row?.campId).filter(Boolean),
    ).size;
    const adGroupCount = new Set(
      rows.map((row) => row?.adGroupId).filter(Boolean),
    ).size;

    const dates = rows
      .map((row) => row?.segDate)
      .filter(Boolean)
      .sort();
    const dateRange = {
      start: dates[0] || "",
      end: dates[dates.length - 1] || "",
    };

    return {
      assetId,
      assetType: this.normalizeAssetType(assetInfo?.assetType || "UNKNOWN"),
      assetFieldType: rows[0]?.assetFieldType || "UNKNOWN",
      assetText: assetInfo?.assetText,
      assetName: assetInfo?.assetName,
      assetImageUrl: assetInfo?.assetImageUrl,
      assetYoutubeVideoId: assetInfo?.assetYoutubeVideoId,

      impressions: totalImpressions,
      clicks: totalClicks,
      cost: totalCost,
      conversions: totalConversions,
      value: totalValue,
      allConv: totalAllConv,
      allValue: totalAllValue,

      ctr,
      cvr,
      roas,
      cpa,
      cpc,

      campaignTypes,
      campaignCount,
      adGroupCount,
      dateRange,
    };
  }

  /**
   * Analyzes effectiveness of different asset field types
   */
  private static analyzeFieldTypeEffectiveness(
    assetMetrics: AssetPerformanceMetrics[],
  ): FieldTypeEffectiveness[] {
    const fieldTypeGroups = new Map<string, AssetPerformanceMetrics[]>();

    assetMetrics.forEach((asset) => {
      if (!fieldTypeGroups.has(asset.assetFieldType)) {
        fieldTypeGroups.set(asset.assetFieldType, []);
      }
      fieldTypeGroups.get(asset.assetFieldType)!.push(asset);
    });

    const effectiveness: FieldTypeEffectiveness[] = [];

    fieldTypeGroups.forEach((assets, fieldType) => {
      const validAssets = assets.filter(
        (asset) => asset.impressions >= this.MIN_IMPRESSIONS_FOR_ANALYSIS,
      );

      if (validAssets.length === 0) return;

      const totalImpressions = validAssets.reduce(
        (sum, asset) => sum + asset.impressions,
        0,
      );
      const totalCost = validAssets.reduce((sum, asset) => sum + asset.cost, 0);
      const totalValue = validAssets.reduce(
        (sum, asset) => sum + asset.value,
        0,
      );

      const avgCtr =
        validAssets.reduce((sum, asset) => sum + asset.ctr, 0) /
        validAssets.length;
      const avgCvr =
        validAssets.reduce((sum, asset) => sum + asset.cvr, 0) /
        validAssets.length;
      const avgRoas =
        validAssets.reduce((sum, asset) => sum + asset.roas, 0) /
        validAssets.length;
      const avgCpa =
        validAssets.reduce((sum, asset) => sum + asset.cpa, 0) /
        validAssets.length;

      // Determine confidence level based on sample size
      let confidenceLevel: "high" | "medium" | "low" = "low";
      if (validAssets.length >= 20 && totalImpressions >= 10000) {
        confidenceLevel = "high";
      } else if (validAssets.length >= 10 && totalImpressions >= 5000) {
        confidenceLevel = "medium";
      }

      effectiveness.push({
        fieldType,
        assetCount: validAssets.length,
        avgCtr,
        avgCvr,
        avgRoas,
        avgCpa,
        totalImpressions,
        totalCost,
        totalValue,
        sampleSize: validAssets.length,
        confidenceLevel,
      });
    });

    return effectiveness.sort((a, b) => b.avgRoas - a.avgRoas);
  }

  /**
   * Analyzes pinned vs rotated asset performance
   */
  private static analyzePinnedAssets(joinedData: any[]): PinnedAssetAnalysis[] {
    // Filter for ad_assets data that has pinning information
    const adAssetsData = joinedData.filter(
      (row) => row.source === "ad_assets" && row.assetFieldType,
    );

    const assetGroups = new Map<string, any[]>();

    adAssetsData.forEach((row) => {
      if (!assetGroups.has(row.assetId)) {
        assetGroups.set(row.assetId, []);
      }
      assetGroups.get(row.assetId)!.push(row);
    });

    const pinnedAnalysis: PinnedAssetAnalysis[] = [];

    assetGroups.forEach((rows, assetId) => {
      const pinnedRows = rows.filter((row) => row.pinnedField);
      const rotatedRows = rows.filter((row) => !row.pinnedField);

      // Only analyze if we have both pinned and rotated data
      if (pinnedRows.length === 0 || rotatedRows.length === 0) return;

      const representative = rows[0];

      const pinnedPerformance = this.calculateContextMetrics(
        pinnedRows,
        assetId,
        representative,
      );
      const rotatedPerformance = this.calculateContextMetrics(
        rotatedRows,
        assetId,
        representative,
      );

      // Only analyze if both have sufficient data
      if (
        pinnedPerformance.impressions < this.MIN_IMPRESSIONS_FOR_ANALYSIS ||
        rotatedPerformance.impressions < this.MIN_IMPRESSIONS_FOR_ANALYSIS
      ) {
        return;
      }

      const ctrDiff = pinnedPerformance.ctr - rotatedPerformance.ctr;
      const cvrDiff = pinnedPerformance.cvr - rotatedPerformance.cvr;
      const roasDiff = pinnedPerformance.roas - rotatedPerformance.roas;

      const pinnedIsBetter = roasDiff > 0;

      // Determine recommendation and confidence
      let recommendation: "pin" | "unpin" | "test_more" = "test_more";
      let confidence: "high" | "medium" | "low" = "low";

      const totalImpressions =
        pinnedPerformance.impressions + rotatedPerformance.impressions;

      if (totalImpressions >= 5000) {
        if (Math.abs(roasDiff) >= 0.5) {
          recommendation = pinnedIsBetter ? "pin" : "unpin";
          confidence = Math.abs(roasDiff) >= 1.0 ? "high" : "medium";
        }
      }

      pinnedAnalysis.push({
        assetId,
        assetInfo: {
          type: this.normalizeAssetType(representative.assetType || "UNKNOWN"),
          fieldType: representative.assetFieldType || "UNKNOWN",
          text: representative.assetText,
          name: representative.assetName,
        },
        pinnedPerformance,
        rotatedPerformance,
        pinnedIsBetter,
        performanceDifference: {
          ctrDiff,
          cvrDiff,
          roasDiff,
        },
        recommendation,
        confidence,
      });
    });

    return pinnedAnalysis.sort(
      (a, b) =>
        Math.abs(b.performanceDifference.roasDiff) -
        Math.abs(a.performanceDifference.roasDiff),
    );
  }

  private static createDetailedAssetList(
    joinedData: any[],
    assetsSheetData: AssetsData[],
  ): AssetPerformanceDetailed[] {
    const assetDetailsMap = new Map<string, AssetsData>();
    assetsSheetData.forEach((ad) => assetDetailsMap.set(ad.assetId, ad));

    const mappedAssets = joinedData
      .map((jd) => {
        const assetDetail = assetDetailsMap.get(jd.assetId);
        const impr = Number(jd.impr || jd.impressions || 0);
        const clicks = Number(jd.clicks || 0);
        const cost = Number(jd.cost || 0);
        const conv = Number(jd.conv || jd.conversions || 0);
        const value = Number(jd.value || jd.conversionValue || 0);
        const allConv = Number(jd.allConv || jd.conversions || 0); // Assuming allConv might fallback to conv
        const allValue = Number(jd.allValue || jd.conversionValue || 0); // Assuming allValue might fallback to value

        return {
          assetId: jd.assetId,
          assetName: assetDetail?.assetName || jd.assetName || "",
          assetType: this.normalizeAssetType(
            assetDetail?.assetType || jd.assetType || "UNKNOWN",
          ),
          assetText: assetDetail?.assetText || jd.assetText || "",
          assetImageUrl: assetDetail?.assetImageUrl || jd.assetImageUrl || "",
          assetYoutubeVideoId:
            assetDetail?.assetYoutubeVideoId || jd.assetYoutubeVideoId || "",
          assetYoutubeVideoTitle:
            assetDetail?.assetYoutubeVideoTitle ||
            jd.assetYoutubeVideoTitle ||
            "",
          assetFieldType: jd.assetFieldType || "UNKNOWN",
          pinnedField: jd.pinnedField || "",
          assetSource: jd.assetSource || jd.sourceContext || "UNKNOWN",

          segDate: jd.segDate || jd.date || "",
          campId: jd.campId || "N/A",
          campaignName: jd.campaignName || "N/A",
          campType: jd.campType || "N/A",
          adGroupId: jd.adGroupId || "N/A",
          adGroupName: jd.adGroupName || "N/A",
          adId: jd.adId || "N/A",

          impressions: impr,
          clicks: clicks,
          cost: cost,
          conversions: conv,
          value: value,
          allConv: allConv,
          allValue: allValue,

          ctr: impr > 0 ? (clicks / impr) * 100 : 0,
          cpc: clicks > 0 ? cost / clicks : 0,
          cpa: conv > 0 ? cost / conv : 0,
          roas: cost > 0 ? value / cost : 0,
          cvr: clicks > 0 ? (conv / clicks) * 100 : 0,
          typeShare: 0, // Initialize with a default value
        };
      })
      .filter((asset) => asset.assetId); // Ensure only assets with an ID are returned

    // Aggregate rows that belong to the same asset within the same context (campId/adGroupId/adId)
    // We intentionally ignore segDate so daily rows are summed into totals.
    const aggregatedMap = new Map<string, AssetPerformanceDetailed>();

    mappedAssets.forEach((asset) => {
      const aggKey = `${asset.assetId}-${asset.campId || ""}-${asset.adGroupId || ""}-${asset.adId || ""}`;

      if (!aggregatedMap.has(aggKey)) {
        aggregatedMap.set(aggKey, { ...asset });
      } else {
        const existing = aggregatedMap.get(aggKey)!;

        // Sum numeric metrics
        existing.impressions += asset.impressions;
        existing.clicks += asset.clicks;
        existing.cost += asset.cost;
        existing.conversions += asset.conversions;
        existing.value += asset.value;
        existing.allConv += asset.allConv;
        existing.allValue += asset.allValue;

        // Prefer known field/type values over UNKNOWN
        if (
          existing.assetFieldType === "UNKNOWN" &&
          asset.assetFieldType !== "UNKNOWN"
        ) {
          existing.assetFieldType = asset.assetFieldType;
        }
        if (existing.assetType === "UNKNOWN" && asset.assetType !== "UNKNOWN") {
          existing.assetType = asset.assetType;
        }
      }
    });

    // Recalculate derived metrics after aggregation
    aggregatedMap.forEach((a) => {
      a.ctr = a.impressions > 0 ? (a.clicks / a.impressions) * 100 : 0;
      a.cpc = a.clicks > 0 ? a.cost / a.clicks : 0;
      a.cpa = a.conversions > 0 ? a.cost / a.conversions : 0;
      a.roas = a.cost > 0 ? a.value / a.cost : 0;
      a.cvr = a.clicks > 0 ? (a.conversions / a.clicks) * 100 : 0;
    });

    // Calculate typeShare after all aggregations are complete
    const totalImpressionsByContext = new Map<string, number>();
    aggregatedMap.forEach((asset) => {
      const contextKey = `${asset.campId || ""}-${asset.adGroupId || ""}`;
      const currentImpressions = totalImpressionsByContext.get(contextKey) || 0;
      totalImpressionsByContext.set(
        contextKey,
        currentImpressions + asset.impressions,
      );
    });

    aggregatedMap.forEach((asset) => {
      const contextKey = `${asset.campId || ""}-${asset.adGroupId || ""}`;
      const totalImpressions = totalImpressionsByContext.get(contextKey);
      if (totalImpressions && totalImpressions > 0) {
        asset.typeShare = (asset.impressions / totalImpressions) * 100;
      } else {
        asset.typeShare = 0;
      }
    });

    return Array.from(aggregatedMap.values());
  }
}
